"use client"

import { useState } from "react"
import { ChevronDown, ChevronRight, BookOpen, Edit2, Trash2, Loader, MoreVertical, Plus } from "lucide-react"
import { postAws } from "../postAws"

export const TopicManagement = ({
  course,
  unsavedTopics,
  updateCourse,
  setUnsavedTopics,
  showToast,
  courseId,
  previewMode,
  openContentMenu,
  selectContent,
  getContentIcon,
  handleDeleteSubtopic,
  handleSequenceChange,
}) => {
  const [editingTopicId, setEditingTopicId] = useState(null)
  const [newTopicTitle, setNewTopicTitle] = useState("")
  const [editingTopic, setEditingTopic] = useState(null)
  const [savingTopicId, setSavingTopicId] = useState(null)
  const [isDeletingTopic, setIsDeletingTopic] = useState(false)
  const [isDeletingSubtopic, setIsDeletingSubtopic] = useState(false)
  const [showContentMenu, setShowContentMenu] = useState(null)
  const [addingTopicId, setAddingTopicId] = useState(null)
  const [draggedContent, setDraggedContent] = useState(null)

  const toggleTopicExpansion = (topicId) => {
    if (topicId.startsWith("unsaved-")) {
      setUnsavedTopics(
        unsavedTopics.map((topic) => (topic.id === topicId ? { ...topic, isExpanded: !topic.isExpanded } : topic)),
      )
    } else {
      updateCourse((prev) => ({
        ...prev,
        topics: prev.topics.map((topic) =>
          topic.id === topicId ? { ...topic, isExpanded: !topic.isExpanded } : topic,
        ),
      }))
    }
  }

  const handleAddTopic = () => {
    // Generate a unique default name
    let newTopicName = "New Chapter"
    let counter = 1
    const allTopics = [...course.topics, ...unsavedTopics]

    // Check if "New Chapter" already exists, if so add a number
    while (allTopics.some((t) => t.title === newTopicName)) {
      newTopicName = `New Chapter ${counter}`
      counter++
    }

    const newTopic = {
      id: `unsaved-${Date.now()}`,
      title: newTopicName,
      isExpanded: true,
      content: [],
    }
    setUnsavedTopics([...unsavedTopics, newTopic])
    setEditingTopicId(newTopic.id)
    setNewTopicTitle(newTopicName)
    setEditingTopic({ id: newTopic.id, title: newTopicName })

    // Show helpful toast
    showToast("New chapter added! Edit the title and add content.", "info")
  }

  const handleTopicTitleSave = async (topicId) => {
    setSavingTopicId(topicId)
    try {
      // Check if a topic with the same title already exists
      const topicTitle = editingTopic ? editingTopic.title : newTopicTitle
      const allTopics = [...course.topics, ...unsavedTopics]
      const duplicateTopic = allTopics.find(
        (t) => t.title.toLowerCase() === topicTitle.toLowerCase() && t.id !== topicId,
      )

      if (duplicateTopic) {
        showToast("You cannot create a chapter with the same name", "error")
        setSavingTopicId(null)
        return
      }

      if (topicId.startsWith("unsaved-")) {
        setAddingTopicId(topicId)
        try {
          // Use the editingTopic.title instead of newTopicTitle
          const topicTitle = editingTopic ? editingTopic.title : newTopicTitle

          const response = await postAws("https://api-dev.aurait.ai/eLearning", "POST", {
            request: "add-topic",
            body: {
              courseId: courseId,
              topicTitle: topicTitle,
            },
          })

          if (response.statusCode === 200) {
            const responseBody = JSON.parse(response.body)
            const newTopic = {
              id: responseBody.topic.id,
              title: responseBody.topic.title,
              isExpanded: true,
              content: [],
            }
            updateCourse((prev) => ({
              ...prev,
              topics: [...prev.topics, newTopic],
            }))
            setUnsavedTopics(unsavedTopics.filter((topic) => topic.id !== topicId))
            showToast("Chapter added successfully")
          } else {
            throw new Error(response.body.message || "Failed to add chapter")
          }
        } catch (error) {
          console.error("Error adding chapter:", error)
          showToast("Error adding chapter. Please try again.", "error")
        } finally {
          setAddingTopicId(null)
        }
      } else {
        const response = await postAws("https://api-dev.aurait.ai/eLearning", "POST", {
          request: "edit-topic",
          body: {
            courseId: courseId,
            topicId: topicId,
            updatedTitle: editingTopic.title,
          },
        })

        if (response.statusCode === 200) {
          updateCourse((prev) => ({
            ...prev,
            topics: prev.topics.map((topic) =>
              topic.id === topicId ? { ...topic, title: editingTopic.title } : topic,
            ),
          }))
          showToast("Chapter updated successfully")
        } else {
          throw new Error(response.body.message || "Failed to update chapter")
        }
      }
    } catch (error) {
      console.error("Error updating chapter:", error)
      showToast("Error updating chapter. Please try again.", "error")
    } finally {
      setSavingTopicId(null)
      setEditingTopic(null)
    }
  }

  const handleDeleteTopic = async (topicId) => {
    if (window.confirm("Are you sure you want to delete this chapter?")) {
      setIsDeletingTopic(true)
      try {
        const response = await postAws("https://api-dev.aurait.ai/eLearning", "POST", {
          request: "delete-topic",
          body: {
            courseId: courseId,
            topicId: topicId,
          },
        })

        if (response.statusCode === 200) {
          updateCourse((prev) => ({
            ...prev,
            topics: prev.topics.filter((topic) => topic.id !== topicId),
          }))
          showToast("Chapter deleted successfully")
        } else {
          throw new Error(response.body.message || "Failed to delete chapter")
        }
      } catch (error) {
        console.error("Error deleting chapter:", error)
        showToast("Error deleting chapter. Please try again.", "error")
      } finally {
        setIsDeletingTopic(false)
      }
    }
  }

  const handleTopicDragStart = (e, topicId) => {
    e.dataTransfer.setData("topic", topicId.toString())
    // Add visual feedback for dragging
    e.currentTarget.classList.add("opacity-50", "border-2", "border-blue-500")
  }

  const handleTopicDragEnd = (e) => {
    // Remove visual feedback
    e.currentTarget.classList.remove("opacity-50", "border-2", "border-blue-500")
  }

  const handleTopicDrop = (e, targetId) => {
    e.preventDefault()
    const draggedId = e.dataTransfer.getData("topic")
    const allTopics = [...course.topics, ...unsavedTopics]
    const draggedIndex = allTopics.findIndex((t) => t.id === draggedId)
    const targetIndex = allTopics.findIndex((t) => t.id === targetId)

    if (draggedIndex !== targetIndex) {
      const [removed] = allTopics.splice(draggedIndex, 1)
      allTopics.splice(targetIndex, 0, removed)

      const newCourseTopics = allTopics.filter((topic) => !topic.id.startsWith("unsaved-"))
      const newUnsavedTopics = allTopics.filter((topic) => topic.id.startsWith("unsaved-"))

      updateCourse((prev) => ({ ...prev, topics: newCourseTopics }))
      setUnsavedTopics(newUnsavedTopics)
      showToast("Chapter order updated", "info")

      // Auto-save the new order
      handleSequenceChange(newCourseTopics)
    }
  }

  const handleContentDragStart = (e, topicId, contentId) => {
    setDraggedContent({ topicId, contentId })
    // Add visual feedback for dragging
    e.currentTarget.classList.add("opacity-50", "border-2", "border-blue-500")
  }

  const handleContentDragEnd = (e) => {
    // Remove visual feedback
    e.currentTarget.classList.remove("opacity-50", "border-2", "border-blue-500")
  }

  const handleContentDrop = (e, targetTopicId, targetIndex) => {
    e.preventDefault()
    if (!draggedContent) return

    const { topicId: sourceTopicId, contentId } = draggedContent
    const allTopics = [...course.topics, ...unsavedTopics]
    const sourceTopic = allTopics.find((t) => t.id === sourceTopicId)
    const targetTopic = allTopics.find((t) => t.id === targetTopicId)

    const contentToMove = sourceTopic.content.find((c) => c.id === contentId)
    sourceTopic.content = sourceTopic.content.filter((c) => c.id !== contentId)

    if (sourceTopicId === targetTopicId) {
      const currentIndex = targetTopic.content.findIndex((c) => c.id === contentId)
      if (currentIndex !== targetIndex) {
        targetTopic.content.splice(targetIndex, 0, contentToMove)
      }
    } else {
      targetTopic.content.splice(targetIndex, 0, contentToMove)
    }

    const newCourseTopics = allTopics.filter((topic) => !topic.id.startsWith("unsaved-"))
    const newUnsavedTopics = allTopics.filter((topic) => topic.id.startsWith("unsaved-"))

    updateCourse((prev) => ({ ...prev, topics: newCourseTopics }))
    setUnsavedTopics(newUnsavedTopics)
    setDraggedContent(null)

    if (sourceTopicId !== targetTopicId) {
      showToast("Content moved to different chapter", "info")
    } else {
      showToast("Content order updated", "info")
    }

    // Auto-save the new order
    handleSequenceChange(newCourseTopics)
  }

  return (
    <div className="p-4">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Course Contents</h2>
        <div className="flex items-center space-x-1"></div>
      </div>

      <div className="space-y-2">
        {[...course.topics, ...unsavedTopics].map((topic, index) => (
          <div
            key={topic.id}
            className={`rounded-md ${topic.isExpanded ? "mb-3" : ""}`}
            draggable={!previewMode}
            onDragStart={(e) => handleTopicDragStart(e, topic.id)}
            onDragEnd={handleTopicDragEnd}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => handleTopicDrop(e, topic.id)}
          >
            <div
              className={`flex items-center p-2.5 rounded-md cursor-pointer ${
                topic.isExpanded ? "bg-blue-50 text-blue-700" : "hover:bg-gray-100 text-gray-700"
              } transition-colors`}
            >
              <div className="flex items-center flex-1 min-w-0" onClick={() => toggleTopicExpansion(topic.id)}>
                {topic.isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-gray-500 mr-2 flex-shrink-0" />
                )}
                <BookOpen
                  className={`h-4 w-4 ${topic.isExpanded ? "text-blue-500" : "text-gray-500"} mr-2 flex-shrink-0`}
                />

                {editingTopic && editingTopic.id === topic.id ? (
                  <div className="flex items-center flex-1 min-w-0">
                    <input
                      type="text"
                      value={editingTopic.title}
                      onChange={(e) => setEditingTopic({ ...editingTopic, title: e.target.value })}
                      className="flex-1 p-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleTopicTitleSave(topic.id)
                      }}
                      className="ml-2 p-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-xs transition-colors"
                      disabled={savingTopicId === topic.id}
                    >
                      {savingTopicId === topic.id ? <Loader className="animate-spin h-3 w-3" /> : "Save"}
                    </button>
                  </div>
                ) : (
                  <span
                    className={`text-sm font-medium ${topic.isExpanded ? "text-blue-700" : "text-gray-700"} truncate`}
                  >
                    {topic.title}
                  </span>
                )}
              </div>

              {!previewMode && !editingTopic && (
                <div className="relative group">
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowContentMenu(showContentMenu === `menu-${topic.id}` ? null : `menu-${topic.id}`)
                    }}
                    className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </button>

                  {showContentMenu === `menu-${topic.id}` && (
                    <div className="absolute right-0 mt-1 w-36 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setEditingTopic(topic)
                          setShowContentMenu(null)
                        }}
                        className="flex items-center w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100"
                      >
                        <Edit2 className="h-3.5 w-3.5 mr-2" />
                        Edit
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDeleteTopic(topic.id)
                          setShowContentMenu(null)
                        }}
                        className="flex items-center w-full px-4 py-2 text-sm text-left text-red-600 hover:bg-red-50"
                        disabled={isDeletingTopic}
                      >
                        {isDeletingTopic ? (
                          <Loader className="animate-spin h-3.5 w-3.5 mr-2" />
                        ) : (
                          <Trash2 className="h-3.5 w-3.5 mr-2" />
                        )}
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {topic.isExpanded && (
              <div className="ml-8 mt-1 space-y-1 pl-2 border-l-2 border-gray-100">
                {(topic.content || []).map((item, itemIndex) => (
                  <div
                    key={item.id}
                    className={`flex items-center text-sm p-2 rounded-md ${
                      false ? "bg-blue-100 text-blue-700" : "text-gray-600 hover:bg-gray-100"
                    } transition-colors cursor-pointer`}
                    draggable={!previewMode}
                    onDragStart={(e) => handleContentDragStart(e, topic.id, item.id)}
                    onDragEnd={handleContentDragEnd}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => handleContentDrop(e, topic.id, itemIndex)}
                    onClick={(e) => {
                      e.stopPropagation()
                      selectContent(topic.id, item.id)
                    }}
                  >
                    <div className="flex items-center flex-1 min-w-0">
                      {getContentIcon(item.type)}
                      <span className="ml-2 truncate">{item.title}</span>
                    </div>

                    {!previewMode && (
                      <div className="relative">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setShowContentMenu(showContentMenu === `submenu-${item.id}` ? null : `submenu-${item.id}`)
                          }}
                          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                        >
                          <MoreVertical className="h-3.5 w-3.5" />
                        </button>

                        {showContentMenu === `submenu-${item.id}` && (
                          <div className="absolute right-0 mt-1 w-36 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                // openEditSubtopicModal(topic.id, item)
                                setShowContentMenu(null)
                              }}
                              className="flex items-center w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100"
                            >
                              <Edit2 className="h-3 w-3 mr-2" />
                              Edit
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDeleteSubtopic(topic.id, item.id)
                                setShowContentMenu(null)
                              }}
                              className="flex items-center w-full px-4 py-2 text-sm text-left text-red-600 hover:bg-red-50"
                              disabled={isDeletingSubtopic}
                            >
                              {isDeletingSubtopic ? (
                                <Loader className="animate-spin h-3 w-3 mr-2" />
                              ) : (
                                <Trash2 className="h-3 w-3 mr-2" />
                              )}
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {!previewMode && (
                  <button
                    onClick={() => openContentMenu(topic.id)}
                    className="flex items-center w-full text-sm text-blue-600 p-2 rounded-md hover:bg-blue-50 transition-colors"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add item
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {!previewMode && (
        <button
          onClick={handleAddTopic}
          className="flex items-center justify-center w-full mt-4 py-2.5 border border-dashed border-gray-300 rounded-md text-blue-600 hover:bg-blue-50 hover:border-blue-400 transition-colors"
        >
          <Plus className="h-4 w-4 mr-2" />
          <span className="text-sm font-medium">Add new chapter</span>
        </button>
      )}
    </div>
  )
}
